import { Box, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import FileUploadBox from './FileUploadBox'

import { dateColDefs } from '@/constants/ag-grid-scenario-builder.constant'
import {
    ACCEPTED_FILE_FORMATS_FOR_LIABILITY_REINVESTMENT_TAXES,
    optimizationDefaultColDefs,
} from '@/constants/portfolioOptimization.constant'
import { useFileParser } from '@/hooks/useFileParser'
import { ICashFlowFileRecord } from '@/interfaces/portfolioOptimization.interface'
import {
    cellValueFormatter,
    portfolioOptimizationDefaultGridOptions,
} from '@/utils/grid-utils/colDefs'
import { GridOptions, ValueFormatterParams } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'

interface LiabilityGridProps {
  onChange: (value: any) => void
  initialData?: ICashFlowFileRecord[]
}

const LiabilityGrid: React.FC<LiabilityGridProps> = ({ onChange, initialData = [] }) => {
  const [cashFlowFileRecords, setCashFlowFileRecords] = useState<
    ICashFlowFileRecord[]
  >(initialData)
  // Grid Configuration
  const gridRef = useRef<AgGridReact>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const gridOptions: GridOptions = {
    ...portfolioOptimizationDefaultGridOptions,
    sideBar: false,
    noRowsOverlayComponentParams: () => {
      return { noRowsMessage: 'No data available. Please upload a file.' }
    },
    rowSelection: 'single' as const,
    columnDefs: [
      {
        ...{ ...optimizationDefaultColDefs, ...dateColDefs },
        floatingFilter: false,
        headerName: 'Flow Date',
        field: 'date',
        cellDataType: 'date',
      },
      {
        ...optimizationDefaultColDefs,
        floatingFilter: false,
        headerName: 'Amount($)',
        field: 'amount',
        cellDataType: 'number',
        filter: 'agNumberColumnFilter',
        valueFormatter: (params: ValueFormatterParams) =>
          cellValueFormatter(params, 2),
      },
    ],
  }

  const handleFileSelect = async (file: File | null) => {
    setSelectedFile(file)
  }

  useEffect(() => {
    onChange(cashFlowFileRecords)
  }, [cashFlowFileRecords])

  useEffect(() => {
    if (selectedFile) {
      parse()
    }
  }, [selectedFile])

  const { data, error, isLoading, parse } = useFileParser({
    file: selectedFile,
  })

  useEffect(() => {
    if (data.length > 0 && !error && !isLoading) {
      const transformedData = data.map((d) => ({
        date: d.column1,
        amount: d.column2,
      }))
      setCashFlowFileRecords(transformedData)
    }
  }, [error, isLoading, data])

  return (
    <Box flex={1}>
      <Box marginBottom={2}>
        <Typography variant='h5'>Optimization Liability</Typography>
        <Typography variant='body2'>
          Upload a File or manually enter the Optimization Liability Values in
          the table below.
        </Typography>
        <FileUploadBox
          id='liability-upload'
          onFileSelect={handleFileSelect}
          acceptedFormats={
            ACCEPTED_FILE_FORMATS_FOR_LIABILITY_REINVESTMENT_TAXES
          }
        />
      </Box>
      <Box className='ag-theme-quartz'>
        <AgGridReact
          ref={gridRef}
          rowData={cashFlowFileRecords}
          gridOptions={gridOptions}
          data-cy='liability-ag-grid'
        ></AgGridReact>
      </Box>
    </Box>
  )
}

export default LiabilityGrid
